import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { Q1_STAY_OPTIONS, Q2_OPTIONS, Q3_VISITS_OPTIONS, COMORBIDITY_OPTIONS, QUESTION_TITLES } from '../../constants/LACERiskScoreConstants';

const Question = ({ title, children, disabled = false }) => (
    <div style={{
        background: '#f8f9fa',
        borderLeft: '4px solid #1bcfb4',
        marginBottom: '25px',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto'
    }}>
        <h5 style={{ color: '#000000ff', marginTop: 0, fontWeight: 600 }}>{title}</h5>
        {children}
    </div>
);

Question.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    disabled: PropTypes.bool
};

const LACERiskScoreModal = ({ isOpen, toggle, onSubmit, onEdit, viewDetails, editId }) => {
    const viewData = viewDetails && viewDetails?.submissions;
    const [q1, setQ1] = useState('');
    const [q1Stay, setQ1Stay] = useState('0');
    const [q2, setQ2] = useState('');
    const [q3Visits, setQ3Visits] = useState('');
    const [comorbidities, setComorbidities] = useState({});
    const [totalScore, setTotalScore] = useState(0);
    const [breakdown, setBreakdown] = useState([]);
    const [showResult, setShowResult] = useState(false);
    const [errors, setErrors] = useState({ q1: '', q2: '', q3: '', q4: '' });
    const [currentMode, setCurrentMode] = useState('new'); // 'new', 'view', 'edit'
    const isViewMode = currentMode === 'view';
    const isEditMode = currentMode === 'edit';

    useEffect(() => {
        if (viewData) {
            setCurrentMode('view');
            const q1Obj = viewData.find(q => q.id === 1);
            if (q1Obj) {
                setQ1(q1Obj.answer);
                if (q1Obj.stay) setQ1Stay(q1Obj.stay);
            }
            const q2Obj = viewData.find(q => q.id === 2);
            if (q2Obj) {
                setQ2(q2Obj.answer === 'no' ? '0' : '3');
            }
            const q3Obj = viewData.find(q => q.id === 3);
            if (q3Obj) {
                setQ3Visits(q3Obj.answer);
            }
            const q4Obj = viewData.find(q => q.id === 4);
            if (q4Obj && q4Obj.answers) {
                const comorbiditiesObj = {};
                q4Obj.answers.forEach((ans, idx) => {
                    comorbiditiesObj[idx] = ans.answer;
                });
                setComorbidities(comorbiditiesObj);
            }
            const total = viewData.reduce((sum, q) => sum + q.points, 0);
            setTotalScore(total);
        } else {
            setCurrentMode('new');
            // reset for new entry
            setQ1('');
            setQ1Stay('0');
            setQ2('');
            setQ3Visits('');
            setComorbidities({});
            setTotalScore(0);
            setBreakdown([]);
            setShowResult(false);
            setErrors({ q1: '', q2: '', q3: '', q4: '' });
        }
    }, [viewData]);

    const handleQ1Change = (e) => {
        setQ1(e.target.value);
        setErrors(prev => ({ ...prev, q1: '' }));
        if (e.target.value === 'no') {
            setQ1Stay('0');
            setQ2('0');
        }
    };

    const handleComorbidityChange = (index, value) => {
        setComorbidities(prev => ({ ...prev, [index]: value }));
    };

    const calculateScore = () => {
        let hasError = false;

        if (q1 === '') {
            setErrors(prev => ({ ...prev, q1: 'Please select if you had hospital admission in last 6 months.' }));
            hasError = true;
        } else if (q1 === 'yes' && q1Stay === '0') {
            setErrors(prev => ({ ...prev, q1: 'Please select the length of stay.' }));
            hasError = true;
        } else {
            setErrors(prev => ({ ...prev, q1: '' }));
        }

        if (q1 === 'yes' && q2 === '') {
            setErrors(prev => ({ ...prev, q2: 'Please select if admitted via emergency department.' }));
            hasError = true;
        } else {
            setErrors(prev => ({ ...prev, q2: '' }));
        }

        if (q3Visits === '') {
            setErrors(prev => ({ ...prev, q3: 'Please select the number of emergency department visits.' }));
            hasError = true;
        } else {
            setErrors(prev => ({ ...prev, q3: '' }));
        }

        const allComorbiditiesAnswered = COMORBIDITY_OPTIONS.every((_, index) => comorbidities[index] !== undefined);
        if (!allComorbiditiesAnswered) {
            setErrors(prev => ({ ...prev, q4: 'Please answer all comorbidity questions.' }));
            hasError = true;
        } else {
            setErrors(prev => ({ ...prev, q4: '' }));
        }

        if (!hasError) {
            let score = 0;
            let bd = [];

            // Q1
            let q1Score = 0;
            if (q1 === 'yes') {
                q1Score = parseInt(q1Stay);
            }
            score += q1Score;
            bd.push({ question: 'Question 1', points: q1Score });

            // Q2
            let q2Score = 0;
            if (q1 === 'yes') {
                q2Score = parseInt(q2);
            }
            score += q2Score;
            bd.push({ question: 'Question 2', points: q2Score });

            // Q3
            const q3Score = parseInt(q3Visits);
            score += q3Score;
            bd.push({ question: 'Question 3', points: q3Score });

            // Q4
            const q4RawScore = COMORBIDITY_OPTIONS.reduce((sum, opt, index) => {
                if (comorbidities[index] === 'yes') {
                    return sum + opt.value;
                }
                return sum;
            }, 0);
            let q4Score = q4RawScore;
            if (q4RawScore >= 4) {
                q4Score = 5;
            }
            score += q4Score;
            bd.push({ question: 'Question 4', points: q4Score, raw: q4RawScore });

            setTotalScore(score);
            setBreakdown(bd);
            setShowResult(true);
        }
    };

    const handleClose = async () => {
        // Compute scores
        let q1Score = 0;
        if (q1 === 'yes') {
            q1Score = parseInt(q1Stay);
        }
        let q2Score = 0;
        if (q1 === 'yes') {
            q2Score = parseInt(q2);
        }
        const q3Score = parseInt(q3Visits);
        const q4RawScore = COMORBIDITY_OPTIONS.reduce((sum, opt, index) => {
            if (comorbidities[index] === 'yes') {
                return sum + opt.value;
            }
            return sum;
        }, 0);
        let q4Score = q4RawScore;
        if (q4RawScore >= 4) {
            q4Score = 5;
        }

        const totalScore = q1Score + q2Score + q3Score + q4Score;
        setTotalScore(totalScore);

        if (isEditMode) {
            const data = {
                questions: [
                    { id: 1, title: QUESTION_TITLES[1], answer: q1, ...(q1 === 'yes' ? { stay: q1Stay } : {}), points: q1Score },
                    { id: 2, title: QUESTION_TITLES[2], answer: q2 === '0' ? 'no' : 'yes', points: q2Score },
                    { id: 3, title: QUESTION_TITLES[3], answer: q3Visits, points: q3Score },
                    {
                        id: 4,
                        title: QUESTION_TITLES[4],
                        answers: COMORBIDITY_OPTIONS.map((opt, index) => ({
                            question: opt.label,
                            answer: comorbidities[index] || 'no',
                            points: (comorbidities[index] === 'yes' ? opt.value : 0),
                            description: opt.description
                        })),
                        points: q4Score
                    }
                ],
                totalScore
            };
            if (onEdit) {
                await onEdit(data, editId);
            }
        } else if (!isViewMode) {
            const data = {
                questions: [
                    { id: 1, title: QUESTION_TITLES[1], answer: q1, ...(q1 === 'yes' ? { stay: q1Stay } : {}), points: q1Score },
                    { id: 2, title: QUESTION_TITLES[2], answer: q2 === '0' ? 'no' : 'yes', points: q2Score },
                    { id: 3, title: QUESTION_TITLES[3], answer: q3Visits, points: q3Score },
                    {
                        id: 4,
                        title: QUESTION_TITLES[4],
                        answers: COMORBIDITY_OPTIONS.map((opt, index) => ({
                            question: opt.label,
                            answer: comorbidities[index] || 'no',
                            points: (comorbidities[index] === 'yes' ? opt.value : 0),
                            description: opt.description
                        })),
                        points: q4Score
                    }
                ],
                totalScore
            };
            if (onSubmit) {
                await onSubmit(data);
            }
        }
        toggle();
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" fullscreen="lg" style={{ fontFamily: "'Roboto', sans-serif", maxWidth: 950 }}>
            <ModalHeader style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)', color: 'black', borderBottom: 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <h1 style={{ color: 'black', margin: 0, fontWeight: 700, fontSize: '1.4em', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>{isEditMode ? 'Edit LACE Risk Score' : 'LACE Risk Score'}</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {isViewMode && <button onClick={() => setCurrentMode('edit')} style={{ background: '#28a745', border: 'none', color: 'white', padding: '0px 15px', borderRadius: '5px', fontWeight: '500' }}><i className="mdi mdi-pencil" style={{ marginRight: '5px' }}></i>Edit</button>}
                        <button onClick={toggle} style={{ background: 'none', border: 'none', fontSize: '1.5em', color: 'black', cursor: 'pointer' }}>×</button>
                    </div>
                </div>
            </ModalHeader>
            <ModalBody style={{ background: '#f5f5f5', padding: '20px', overflowY: 'auto' }}>
                <div style={{ margin: '0 auto' }}>
                    {showResult ? (
                        <div style={{ textAlign: 'center', padding: '20px', background: 'linear-gradient(135deg, #f5f5f5 0%, #e8f4f8 100%)', minHeight: '500px' }}>
                            <div style={{
                                background: '#fff',
                                borderRadius: '16px',
                                padding: '30px',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                                marginBottom: '30px',
                                position: 'relative'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-20px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    background: '#4285F4',
                                    color: 'white',
                                    padding: '10px 20px',
                                    borderRadius: '20px',
                                    fontWeight: 'bold',
                                    fontSize: '14px'
                                }}>
                                    LACE Risk Assessment
                                </div>
                                <h1 style={{ color: '#4285F4', fontSize: '4em', margin: '20px 0 10px 0', fontWeight: '700' }}>{totalScore}</h1>
                                <p style={{ color: '#5f6368', fontSize: '18px', margin: '0', fontWeight: '500' }}>Total Risk Points</p>
                                <div style={{
                                    marginTop: '20px',
                                    padding: '10px',
                                    background: totalScore <= 4 ? '#e8f5e8' : totalScore <= 9 ? '#fff3e0' : '#ffebee',
                                    borderRadius: '8px',
                                    color: totalScore <= 4 ? '#2e7d32' : totalScore <= 9 ? '#f57c00' : '#c62828',
                                    fontWeight: 'bold'
                                }}>
                                    {totalScore <= 4 ? 'Low Risk' : totalScore <= 9 ? 'Moderate Risk' : 'High Risk'}
                                </div>
                            </div>

                            <div style={{
                                background: '#fff',
                                borderRadius: '16px',
                                padding: '25px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                marginBottom: '20px'
                            }}>
                                <h3 style={{ color: '#4285F4', marginBottom: '20px', fontWeight: '600' }}>
                                    <i className="mdi mdi-chart-pie" style={{ marginRight: '10px' }}></i>
                                    Score Breakdown
                                </h3>
                                {breakdown.map((item, index) => (
                                    <div key={index} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '15px 0',
                                        borderBottom: index < breakdown.length - 1 ? '1px solid #f0f0f0' : 'none'
                                    }}>
                                        <span style={{ color: '#5f6368', fontSize: '16px' }}>{item.question}</span>
                                        <div style={{
                                            background: '#4285F4',
                                            color: 'white',
                                            padding: '5px 12px',
                                            borderRadius: '20px',
                                            fontWeight: 'bold',
                                            fontSize: '14px'
                                        }}>
                                            {item.points} pts
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button onClick={() => setShowResult(false)} className="btn btn-outline-secondary" style={{ flex: 1, padding: '12px', borderRadius: '8px', fontWeight: '500' }}>
                                    <i className="mdi mdi-arrow-left"></i> &nbsp; Back
                                </button>
                                <button onClick={handleClose} className="text-white btn btn-info" style={{ flex: 1, padding: '12px', borderRadius: '8px', fontWeight: '500' }}>
                                    <i className="mdi mdi-close"></i> &nbsp; Close
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isViewMode && (
                                <div style={{
                                    background: '#fff',
                                    borderRadius: '14px',
                                    padding: '16px 22px',
                                    marginBottom: '25px',
                                    boxShadow: '0 6px 16px rgba(66,133,244,0.15)',
                                    border: '1px solid rgba(66,133,244,0.25)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '15px'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ color: '#5f6368', fontSize: '16px', fontWeight: '600' }}>
                                            Total LACE Risk Score: &nbsp;
                                        </span>
                                        <span style={{ color: '#4285F4', fontSize: '22px', fontWeight: '800' }}>
                                            {viewDetails?.totalScore ?? '-'}
                                        </span>
                                    </div>
                                    <div style={{
                                        padding: '8px 16px',
                                        background: (viewDetails?.totalScore ?? 0) <= 4 ? '#e8f5e8' : (viewDetails?.totalScore ?? 0) <= 9 ? '#fff3e0' : '#ffebee',
                                        borderRadius: '8px',
                                        color: (viewDetails?.totalScore ?? 0) <= 4 ? '#2e7d32' : (viewDetails?.totalScore ?? 0) <= 9 ? '#f57c00' : '#c62828',
                                        fontWeight: 'bold',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {(viewDetails?.totalScore ?? 0) <= 4 ? 'Low Risk' : (viewDetails?.totalScore ?? 0) <= 9 ? 'Moderate Risk' : 'High Risk'}
                                    </div>
                                </div>
                            )}
                            <Question title={`${QUESTION_TITLES[1]}`}>
                                <label style={{fontSize : '16px'}}><input type="radio" name="q1" value="no" checked={q1 === 'no'} onChange={handleQ1Change} disabled={isViewMode}/> No (0 points)</label><br />
                                <label style={{fontSize : '16px'}}><input type="radio" name="q1" value="yes" checked={q1 === 'yes'} onChange={handleQ1Change} disabled={isViewMode}/> Yes</label>
                                <select value={q1Stay} onChange={(e) => { setQ1Stay(e.target.value); setErrors(prev => ({ ...prev, q1: '' })); }} disabled={isViewMode || q1 === 'no'} style={{ padding: '10px 12px', border: '2px solid #dadce0', borderRadius: '8px', background: '#fff', marginLeft: '12px', fontSize: '16px', minWidth: '30%' }}>
                                    {Q1_STAY_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {errors.q1 && <p style={{ color: 'red' }}>{errors.q1}</p>}
                            </Question>

                            <Question title={`${QUESTION_TITLES[2]}`} disabled={q1 === 'no'}>
                                <p style={{ color: '#5f6368', fontSize: '14px' }}>(Only if Question 1 is Yes)</p>
                                <p style={{ color: '#333', fontSize: '16px', display: 'block', marginTop: '-10px', fontWeight: 800 }}>Was the patient admitted to hospital via the emergency department?</p>
                                {Q2_OPTIONS.map(option => (
                                    <React.Fragment key={option.value}>
                                        <label style={{fontSize : '16px'}}><input type="radio" name="q2" value={option.value} checked={q2 === option.value} onChange={(e) => { setQ2(e.target.value); setErrors(prev => ({ ...prev, q2: '' })); }} disabled={isViewMode || q1 === 'no'} /> {option.label}</label><br />
                                    </React.Fragment>
                                ))}
                                {errors.q2 && <p style={{ color: 'red' }}>{errors.q2}</p>}
                            </Question>

                            <Question title={`${QUESTION_TITLES[3]}`}>
                                <p style={{ color: '#333', fontSize: '16px', fontWeight: 800 }}>How many times has the patient visited an emergency department in the last six months?</p>
                                <select value={q3Visits} onChange={(e) => { setQ3Visits(e.target.value); setErrors(prev => ({ ...prev, q3: '' })); }} disabled={isViewMode} style={{ padding: '10px 12px', border: '2px solid #dadce0', borderRadius: '8px', background: '#fff', fontSize: '16px', minWidth: '30%' }}>
                                    {Q3_VISITS_OPTIONS.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                                {errors.q3 && <p style={{ color: 'red' }}>{errors.q3}</p>}
                            </Question>

                            <Question title={`${QUESTION_TITLES[4]}`}>
                                {COMORBIDITY_OPTIONS.map((option, index) => (
                                    <React.Fragment key={option.label}>
                                        <div style={{ marginBottom: '15px' }}>
                                            <span style={{ color: '#333', fontSize: '16px', fontWeight: 700 }}>{index + 1}. {option.label}</span>
                                            <span style={{ color: '#666', fontSize: '15px', display: 'block', marginBottom: '8px', marginTop: '5px' }}>{option.description}</span>
                                            <div style={{ color: '#666', marginTop: '2px' }}>
                                                <label style={{fontSize : '16px'}}><input type="radio" name={`comorbidity-${index}`} value="yes" checked={comorbidities[index] === 'yes'} onChange={() => handleComorbidityChange(index, 'yes')} disabled={isViewMode} /> Yes</label>
                                                <label style={{ marginLeft: '20px', fontSize : '16px' }}><input type="radio" name={`comorbidity-${index}`} value="no" checked={comorbidities[index] === 'no'} onChange={() => handleComorbidityChange(index, 'no')} disabled={isViewMode} /> No</label>
                                            </div>
                                        </div>
                                    </React.Fragment>
                                ))}
                                {errors.q4 && <p style={{ color: 'red' }}>{errors.q4}</p>}
                            </Question>

                            <button onClick={isViewMode ? handleClose : (isEditMode ? handleClose : calculateScore)} className="text-white float-right btn btn-info btn-icon-text create-note-click-cls" style={{ width: '100%' }}>
                                <i className="mdi mdi-clipboard-plus"></i> &nbsp; {isViewMode ? 'Close' : (isEditMode ? 'Update' : 'Submit')}
                            </button>
                        </>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};

LACERiskScoreModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    onEdit: PropTypes.func,
    viewDetails: PropTypes.object,
    editId: PropTypes.string
};

export default LACERiskScoreModal;
